import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Zone } from "../../models/zone.model";
import { zoneService } from "../../service/zone.service";
import PageHeader from "../PageHeader";

const ZoneComponent: React.FC = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [zone, setZone] = useState<Zone>({
    name: "",
    description: "",
    isActive: true,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("zonesTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("zonesTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>(() => {
    return localStorage.getItem("zonesTableSortField") || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(() => {
    const savedOrder = localStorage.getItem("zonesTableSortOrder");
    return savedOrder ? (parseInt(savedOrder) as 1 | -1 | 0) : 1;
  });
  useEffect(() => {
    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rows, sortField, sortOrder, globalFilter]);

  const loadZones = async () => {
    setLoading(true);
    try {
      const result = await zoneService.getAllPaginated({
        pageNumber: page,
        pageSize: rows,
        sortField,
        sortOrder: sortOrder === -1 ? "desc" : "asc",
        globalFilter,
      });
      setZones(
        (result?.items ?? []).map((item: any) => ({
          ...item,
          id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
        })) || []
      );
      setTotalRecords(result?.totalCount ?? 0);
    } catch (err) {
      console.error("Error fetching zones", err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (zone.id) {
        await zoneService.update(zone.id, zone);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Zone updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        await zoneService.create(zone);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Zone created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      // Reload zones without resetting pagination
      await loadZones();
      setShowForm(false);
    } catch (err: any) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save zone details.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
    setLoading(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setZone({ ...zone, [name]: value });
  };

  const handleEdit = (rowData: Zone) => {
    setZone(rowData);
    setShowForm(true);
  };

  const handleStatus = async (rowData: Zone) => {
    if (!rowData.id) return;
    try {
      await zoneService.updateStatus(rowData.id);
      await loadZones();
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Zone status updated successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to update zone status.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const actionBodyTemplate = (rowData: Zone) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleEdit(rowData)}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleStatus(rowData)}
        title="Status"
        style={{ color: rowData.isActive ? "#198754" : "#dc3545" }}
      >
        {rowData.isActive ? <Icon.Unlock size={20} /> : <Icon.Lock size={20} />}
      </Button>
    </div>
  );

  return (
    <div className="content has-nobreadcrumbs">
      <PageHeader
        title="Zone List"
        showBackButton={false}
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={loadZones}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Zones"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3 flex-wrap">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("zonesTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  variant="primary"
                  onClick={() => {
                    setZone({
                      name: "",
                      description: "",
                      isActive: true,
                    });
                    setShowForm(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Zone
                </Button>
              </div>

              <DataTable
                value={zones}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  setPage(newPage);
                  setRows(e.rows);
                  localStorage.setItem("zonesTablePage", newPage.toString());
                  localStorage.setItem("zonesTableRows", e.rows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1 | 0;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("zonesTableSortField", newSortField);
                  localStorage.setItem(
                    "zonesTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={loading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No zones found."
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showForm && (
                <div className="modal show d-block" tabIndex={-1}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {zone.id ? "Update Zone" : "Create Zone"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowForm(false)}
                          title="Close"
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name:</label>
                            <input
                              className="form-control"
                              name="name"
                              value={zone.name}
                              onChange={handleChange}
                              placeholder="Enter zone name"
                              title="Zone name"
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description:</label>
                            <textarea
                              className="form-control"
                              name="description"
                              value={zone.description || ""}
                              onChange={handleChange}
                              placeholder="Enter zone description"
                              title="Zone description"
                              rows={3}
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? zone.id
                                ? "Updating..."
                                : "Creating..."
                              : zone.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneComponent;
